/*
  Filtres client de la grille collection. Le masquage passe par la classe
  .is-filtered-out pour ne pas toucher au hidden posé par la navigation.
*/
(() => {
  const w = /** @type {any} */ (window);
  if (w.__mainCollectionFiltersInitialized) return;
  w.__mainCollectionFiltersInitialized = true;

  const NEUTRAL_DOT = '#d4d4d4';

  const init = () => {
    const root = document.querySelector('[data-collection-filters]');
    const list = document.querySelector('.main-collection__list');
    if (!root || !list) return;

    const SIZE_ORDER = (root.dataset.sizeOrder || 'XS,S,M,L,XL')
      .split(',')
      .map((v) => v.trim().toUpperCase())
      .filter(Boolean);

    const emptyMessage = document.querySelector('[data-filter-empty]');
    const resetButton = root.querySelector('[data-filter-reset]');

    const state = {
      colors: new Set(),
      sizes: new Set(),
      transmission: new Set(),
      model: new Set(),
      priceMin: null,
      priceMax: null,
    };

    let bounds = { min: 0, max: 0 };

    const getItems = () => Array.from(list.querySelectorAll('.main-collection__item'));

    const parseList = (csv) => (csv || '').split('|').map((v) => v.trim()).filter(Boolean);

    const readItem = (li) => ({
      price: Math.round(Number(li.dataset.price || 0) / 100),
      colors: parseList(li.dataset.colors),
      sizes: parseList(li.dataset.sizes),
      transmission: (li.dataset.transmission || '').trim(),
      model: (li.dataset.model || '').trim(),
    });

    /* les pastilles de variantes du thème portent --swatch-background, les nôtres --swatch-color */
    const harvestColorHexes = () => {
      const map = new Map();
      list.querySelectorAll('[data-swatch-tooltip]').forEach((node) => {
        const name = node.dataset.swatchTooltip;
        if (!name || map.has(name)) return;
        let value = node.style.getPropertyValue('--swatch-color').trim();
        if (!value) {
          const swatch = node.querySelector('.swatch');
          value = swatch ? swatch.style.getPropertyValue('--swatch-background').trim() : '';
        }
        if (value && value !== 'null') map.set(name, value);
      });
      return map;
    };

    const collectOptions = () => {
      const colors = new Map();
      const sizes = new Map();
      const transmission = new Map();
      const model = new Map();
      let min = Infinity;
      let max = -Infinity;

      for (const li of getItems()) {
        const d = readItem(li);
        if (d.price > 0) {
          min = Math.min(min, d.price);
          max = Math.max(max, d.price);
        }
        d.colors.forEach((c) => colors.set(c, (colors.get(c) || 0) + 1));
        d.sizes.forEach((c) => sizes.set(c, (sizes.get(c) || 0) + 1));
        if (d.transmission) transmission.set(d.transmission, (transmission.get(d.transmission) || 0) + 1);
        if (d.model) model.set(d.model, (model.get(d.model) || 0) + 1);
      }

      if (!Number.isFinite(min)) { min = 0; max = 0; }
      return { colors, sizes, transmission, model, min, max };
    };

    const sortSizes = (values) => {
      return values.sort((a, b) => {
        const ia = SIZE_ORDER.indexOf(a);
        const ib = SIZE_ORDER.indexOf(b);
        if (ia !== -1 && ib !== -1) return ia - ib;
        if (ia !== -1) return -1;
        if (ib !== -1) return 1;
        return a.localeCompare(b, 'fr');
      });
    };

    const formatPrice = (euros) => `${euros.toLocaleString('fr-FR')} €`;

    const renderOptionRow = (groupKey, value, label, dotHex) => {
      const row = document.createElement('label');
      row.className = 'collection-filters__option';

      const nameSpan = document.createElement('span');
      nameSpan.className = 'collection-filters__option-name';
      if (dotHex !== undefined) {
        const dot = document.createElement('span');
        dot.className = 'collection-filters__option-dot';
        dot.style.setProperty('--swatch-color', dotHex || NEUTRAL_DOT);
        nameSpan.appendChild(dot);
      }
      nameSpan.appendChild(document.createTextNode(label));

      const input = document.createElement('input');
      input.type = 'checkbox';
      input.className = 'collection-filters__option-input';
      input.value = value;
      input.checked = state[groupKey].has(value);
      input.addEventListener('change', () => {
        if (input.checked) state[groupKey].add(value);
        else state[groupKey].delete(value);
        apply();
      });

      row.appendChild(nameSpan);
      row.appendChild(input);
      return row;
    };

    const renderOptions = () => {
      const options = collectOptions();
      const hexes = harvestColorHexes();

      const groups = [
        { key: 'colors', entries: Array.from(options.colors.keys()).sort((a, b) => a.localeCompare(b, 'fr')), dot: true },
        { key: 'sizes', entries: sortSizes(Array.from(options.sizes.keys())), dot: false },
        { key: 'transmission', entries: Array.from(options.transmission.keys()).sort((a, b) => a.localeCompare(b, 'fr')), dot: false },
        { key: 'model', entries: Array.from(options.model.keys()).sort((a, b) => a.localeCompare(b, 'fr')), dot: false },
      ];

      for (const group of groups) {
        const wrap = root.querySelector(`[data-filter-group="${group.key}"]`);
        if (!wrap) continue;
        const panel = wrap.querySelector('[data-filter-options]');
        if (!panel) continue;
        panel.innerHTML = '';

        for (const selected of Array.from(state[group.key])) {
          if (!group.entries.includes(selected)) state[group.key].delete(selected);
        }

        for (const value of group.entries) {
          panel.appendChild(renderOptionRow(group.key, value, value, group.dot ? hexes.get(value) : undefined));
        }

        wrap.hidden = group.entries.length === 0;
      }

      setupPriceSlider(options.min, options.max);
    };

    /* --- Slider de prix --- */
    const priceGroup = root.querySelector('[data-filter-group="price"]');
    const priceInputMin = priceGroup ? priceGroup.querySelector('[data-price-input-min]') : null;
    const priceInputMax = priceGroup ? priceGroup.querySelector('[data-price-input-max]') : null;
    const priceLabelMin = priceGroup ? priceGroup.querySelector('[data-price-label-min]') : null;
    const priceLabelMax = priceGroup ? priceGroup.querySelector('[data-price-label-max]') : null;
    const priceFill = priceGroup ? priceGroup.querySelector('[data-price-fill]') : null;

    const STEP = 50;

    const updatePriceUi = () => {
      if (!priceInputMin || !priceInputMax) return;
      const lo = Number(priceInputMin.value);
      const hi = Number(priceInputMax.value);
      if (priceLabelMin) priceLabelMin.textContent = formatPrice(lo);
      if (priceLabelMax) priceLabelMax.textContent = formatPrice(hi);
      if (priceFill && bounds.max > bounds.min) {
        const span = bounds.max - bounds.min;
        priceFill.style.left = `${((lo - bounds.min) / span) * 100}%`;
        priceFill.style.right = `${100 - ((hi - bounds.min) / span) * 100}%`;
      }
    };

    const setupPriceSlider = (min, max) => {
      if (!priceGroup || !priceInputMin || !priceInputMax) return;
      const lo = Math.floor(min / STEP) * STEP;
      const hi = Math.ceil(max / STEP) * STEP;
      bounds = { min: lo, max: hi };
      priceGroup.hidden = hi <= lo;

      for (const input of [priceInputMin, priceInputMax]) {
        input.min = String(lo);
        input.max = String(hi);
        input.step = String(STEP);
      }
      priceInputMin.value = String(state.priceMin !== null ? Math.max(lo, state.priceMin) : lo);
      priceInputMax.value = String(state.priceMax !== null ? Math.min(hi, state.priceMax) : hi);
      updatePriceUi();
    };

    const onPriceInput = (which) => () => {
      if (!priceInputMin || !priceInputMax) return;
      let lo = Number(priceInputMin.value);
      let hi = Number(priceInputMax.value);
      if (lo > hi) {
        if (which === 'min') { hi = lo; priceInputMax.value = String(hi); }
        else { lo = hi; priceInputMin.value = String(lo); }
      }
      state.priceMin = lo > bounds.min ? lo : null;
      state.priceMax = hi < bounds.max ? hi : null;
      updatePriceUi();
      apply();
    };

    if (priceInputMin) priceInputMin.addEventListener('input', onPriceInput('min'));
    if (priceInputMax) priceInputMax.addEventListener('input', onPriceInput('max'));

    /* --- Application des filtres --- */
    const matches = (li) => {
      const d = readItem(li);
      if (state.priceMin !== null && d.price < state.priceMin) return false;
      if (state.priceMax !== null && d.price > state.priceMax) return false;
      if (state.colors.size && !d.colors.some((c) => state.colors.has(c))) return false;
      if (state.sizes.size && !d.sizes.some((c) => state.sizes.has(c))) return false;
      if (state.transmission.size && !state.transmission.has(d.transmission)) return false;
      if (state.model.size && !state.model.has(d.model)) return false;
      return true;
    };

    const isActive = () =>
      state.colors.size > 0 ||
      state.sizes.size > 0 ||
      state.transmission.size > 0 ||
      state.model.size > 0 ||
      state.priceMin !== null ||
      state.priceMax !== null;

    const updateCounts = () => {
      const counts = {
        price: state.priceMin !== null || state.priceMax !== null ? 1 : 0,
        colors: state.colors.size,
        sizes: state.sizes.size,
        transmission: state.transmission.size,
        model: state.model.size,
      };
      for (const [key, count] of Object.entries(counts)) {
        const wrap = root.querySelector(`[data-filter-group="${key}"]`);
        if (!wrap) continue;
        const badge = wrap.querySelector('[data-filter-count]');
        const toggle = wrap.querySelector('[data-filter-toggle]');
        if (badge) badge.textContent = count > 0 && key !== 'price' ? ` (${count})` : '';
        if (toggle) toggle.classList.toggle('is-active', count > 0);
      }
      if (resetButton) resetButton.hidden = !isActive();
    };

    const apply = () => {
      let visible = 0;
      for (const li of getItems()) {
        const ok = matches(li);
        li.classList.toggle('is-filtered-out', !ok);
        if (ok && !li.hidden) visible += 1;
      }
      if (emptyMessage) emptyMessage.hidden = visible > 0;
      updateCounts();
    };

    if (resetButton) {
      resetButton.addEventListener('click', () => {
        state.colors.clear();
        state.sizes.clear();
        state.transmission.clear();
        state.model.clear();
        state.priceMin = null;
        state.priceMax = null;
        renderOptions();
        apply();
      });
    }

    /* --- Ouverture / fermeture des panneaux --- */
    const closeAll = (except) => {
      root.querySelectorAll('[data-filter-group]').forEach((wrap) => {
        if (wrap === except) return;
        const toggle = wrap.querySelector('[data-filter-toggle]');
        const panel = wrap.querySelector('[data-filter-panel]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
        if (panel) panel.hidden = true;
      });
    };

    root.querySelectorAll('[data-filter-group]').forEach((wrap) => {
      const toggle = wrap.querySelector('[data-filter-toggle]');
      const panel = wrap.querySelector('[data-filter-panel]');
      if (!toggle || !panel) return;
      toggle.addEventListener('click', () => {
        const open = panel.hidden;
        closeAll(wrap);
        panel.hidden = !open;
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Node) || !root.contains(event.target)) closeAll(null);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeAll(null);
    });

    /* la navigation peut injecter des pages supplémentaires en différé */
    let rebuildTimer = null;
    const observer = new MutationObserver(() => {
      if (rebuildTimer) clearTimeout(rebuildTimer);
      rebuildTimer = setTimeout(() => {
        renderOptions();
        apply();
      }, 150);
    });
    observer.observe(list, { childList: true });

    renderOptions();
    apply();
    root.hidden = false;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
